import type {
  StayCalendar,
  StayCalendarReservation,
  StayGap,
  StaysBlock,
} from '../../../../interfaces/stays';
import { addDays, addMonths, daysBetween, formatDayKey } from '../../../../function-utils/building-time';
import { digitsOf, formatMoney } from '../../../../utils/stays-money';
import { fullDayName } from '../../../shared/month-grid/month-model';
import { nightsLabel, sourceText } from '../reservations/reservation-labels';

/**
 * THE OPERATIONS CALENDAR'S DAY ROLES (plan S4.10) — pure, no React.
 *
 * Every value is a building day key ('2026-10-14'); a stay's check_out and a
 * block's end_date are EXCLUSIVE (the morning the unit is free again), so a
 * stay of 14 → 17 Oct has the nights 14, 15 and 16 and departs on the 17th.
 * Nothing here builds a JS date: the keys move through addDays / daysBetween.
 */

export type CalendarTurnaround = StayCalendar['turnarounds'][number];

export interface StayNight {
  stay: StayCalendarReservation;
  /** 1-based: the night this day is. */
  index: number;
  of: number;
  first: boolean;
}

export interface DayRoles {
  /** The stay sleeping here tonight (check_in ≤ day < check_out). */
  night: StayNight | null;
  /** The stay leaving this morning (check_out = day). */
  departure: StayCalendarReservation | null;
  /** An owner block covering tonight. */
  block: StaysBlock | null;
  /** A channel (provider) block covering tonight. */
  provider: StaysBlock | null;
  /** Tonight is an empty night of a gap run of `nights`. */
  gap: { run: StayGap; nights: number } | null;
  turnaround: CalendarTurnaround | null;
}

export const EMPTY_ROLES: DayRoles = {
  night: null,
  departure: null,
  block: null,
  provider: null,
  gap: null,
  turnaround: null,
};

export interface CalendarIndex {
  days: Map<string, DayRoles>;
  stays: Map<string, StayCalendarReservation>;
}

const MONTH_KEY = /^\d{4}-\d{2}$/;

/** The month after a 'YYYY-MM' key. */
export const nextMonthKey = (month: string): string =>
  MONTH_KEY.test(month) ? addMonths(`${month}-01`, 1).slice(0, 7) : month;

/** The months of a window — the server's, or `count` from `from` when it sent none. */
export const monthKeysOf = (data: StayCalendar): string[] => data.months.map(m => m.key);

/** Overlapping or touching gap runs become one run (a run cut at a window's edge). */
export const mergeGaps = (gaps: StayGap[]): StayGap[] => {
  const sorted = [...gaps].filter(g => g.start < g.end).sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  const out: StayGap[] = [];
  for (const g of sorted) {
    const last = out[out.length - 1];
    if (last && g.start <= last.end) {
      const end = g.end > last.end ? g.end : last.end;
      out[out.length - 1] = { start: last.start, end, nights: daysBetween(last.start, end) };
    } else {
      out.push({ start: g.start, end: g.end, nights: daysBetween(g.start, g.end) });
    }
  }

  return out;
};

const byId = <T extends { id: string }>(lists: T[][]): T[] => {
  const map = new Map<string, T>();
  for (const list of lists) for (const item of list) map.set(item.id, item);

  return Array.from(map.values());
};

/** The windows on screen ("Show 6 more months" appends one) as ONE calendar. */
export const mergeCalendars = (windows: StayCalendar[]): StayCalendar | null => {
  if (!windows.length) return null;
  const first = windows[0];
  const months = new Map<string, StayCalendar['months'][number]>();
  const turnarounds = new Map<string, CalendarTurnaround>();
  for (const w of windows) {
    for (const m of w.months) if (!months.has(m.key)) months.set(m.key, m);
    for (const t of w.turnarounds) turnarounds.set(`${t.date}|${t.out_id}|${t.in_id}`, t);
  }

  return {
    ...first,
    months: Array.from(months.values()).sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)),
    reservations: byId(windows.map(w => w.reservations)),
    blocks: byId(windows.map(w => w.blocks)),
    gaps: mergeGaps(windows.flatMap(w => w.gaps)),
    turnarounds: Array.from(turnarounds.values()),
  };
};

const rolesAt = (days: Map<string, DayRoles>, day: string): DayRoles => {
  let roles = days.get(day);
  if (!roles) {
    roles = { ...EMPTY_ROLES };
    days.set(day, roles);
  }

  return roles;
};

/** A safety cap on one range's nights (a stay is at most 365; a gap may be longer). */
const MAX_RANGE = 800;

export const indexCalendar = (data: StayCalendar): CalendarIndex => {
  const days = new Map<string, DayRoles>();
  const stays = new Map<string, StayCalendarReservation>();

  for (const r of data.reservations) {
    stays.set(r.id, r);
    const of = Math.max(1, daysBetween(r.check_in, r.check_out) || r.nights || 1);
    for (let i = 0; i < Math.min(of, MAX_RANGE); i += 1) {
      rolesAt(days, addDays(r.check_in, i)).night = { stay: r, index: i + 1, of, first: i === 0 };
    }
    rolesAt(days, r.check_out).departure = r;
  }

  for (const b of data.blocks) {
    const n = Math.max(0, daysBetween(b.start_date, b.end_date) || 0);
    for (let i = 0; i < Math.min(n, MAX_RANGE); i += 1) {
      const roles = rolesAt(days, addDays(b.start_date, i));
      if (b.kind === 'provider') roles.provider = b;
      else roles.block = b;
    }
  }

  for (const g of data.gaps) {
    const n = Math.max(0, daysBetween(g.start, g.end) || 0);
    for (let i = 0; i < Math.min(n, MAX_RANGE); i += 1) {
      rolesAt(days, addDays(g.start, i)).gap = { run: g, nights: n };
    }
  }

  for (const t of data.turnarounds) rolesAt(days, t.date).turnaround = t;

  return { days, stays };
};

/** What a day holds. */
export const rolesOf = (index: CalendarIndex, day: string): DayRoles => index.days.get(day) || EMPTY_ROLES;

/** The same, straight from a calendar (tests, one-off reads). */
export const dayRoles = (day: string, data: StayCalendar): DayRoles => rolesOf(indexCalendar(data), day);

/* ─────────────────────────────  the words  ───────────────────────────── */

/** "Ana P." */
export const guestName = (r: Pick<StayCalendarReservation, 'lead_first_name' | 'lead_last_initial'>): string => {
  const first = (r.lead_first_name || '').trim() || 'Guest';
  const initial = (r.lead_last_initial || '').trim().charAt(0).toUpperCase();

  return initial ? `${first} ${initial}.` : first;
};

/** "€420" for a whole amount, "€420.50" otherwise; '' when none is recorded. */
export const compactMoney = (cents: number | null | undefined, currency: string): string => {
  if (cents == null) return '';
  const digits = digitsOf(currency);
  if (digits > 0 && cents % 10 ** digits === 0) {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(cents / 10 ** digits);
    } catch {
      return formatMoney(cents, currency);
    }
  }

  return formatMoney(cents, currency);
};

/** The first night: "In 15:00 · Airbnb · Ana P. · 3 nights · €420". */
export const firstNightText = (r: StayCalendarReservation): string => {
  const money = compactMoney(r.gross_cents, r.currency);

  return [`In ${r.arrival_time}`, sourceText(r.source), guestName(r), nightsLabel(r.nights), money]
    .filter(Boolean)
    .join(' · ');
};

/** A later night: "Ana P. · 2/3". */
export const laterNightText = (n: StayNight): string => `${guestName(n.stay)} · ${n.index}/${n.of}`;

/** "Out 11:00 · Ana P." */
export const departureText = (r: StayCalendarReservation): string => `Out ${r.departure_time} · ${guestName(r)}`;

/** "Closed · Painting" */
export const blockText = (b: StaysBlock): string => `Closed · ${(b.note || '').trim() || 'Owner block'}`;

export const PROVIDER_TEXT = 'Channel · unavailable';

/** "1-night gap" */
export const gapText = (nights: number): string => `${nights}-night gap`;

/** A turnaround of two hours or less is tight. */
export const URGENT_TURNAROUND_HOURS = 2;

export interface FooterLine {
  text: string;
  urgent: boolean;
  kind: 'turnaround' | 'clean';
}

/** The departure-day footer: "Turnaround 11:00 → 15:00 · 4 h", or "Clean → ready". */
export const footerOf = (roles: DayRoles, index: CalendarIndex, data: Pick<StayCalendar, 'check_in_time' | 'check_out_time'>): FooterLine | null => {
  const t = roles.turnaround;
  if (t) {
    const out = index.stays.get(t.out_id);
    const inn = index.stays.get(t.in_id);
    const from = (out && out.departure_time) || data.check_out_time;
    const to = (inn && inn.arrival_time) || data.check_in_time;

    return {
      text: `Turnaround ${from} → ${to} · ${t.window_hours} h`,
      urgent: t.window_hours <= URGENT_TURNAROUND_HOURS,
      kind: 'turnaround',
    };
  }
  if (roles.departure) return { text: 'Clean → ready', urgent: false, kind: 'clean' };

  return null;
};

/**
 * The day button's accessible name — every word the cell shows, so the phone's
 * compact cell (marks only) loses nothing:
 * "Wednesday 14 October 2026, Ana P. arrives 15:00, Airbnb, 3 nights".
 */
export const dayLabelOf = (day: string, roles: DayRoles, index: CalendarIndex, data: StayCalendar): string => {
  const parts: string[] = [fullDayName(day)];
  if (day === data.building_today) parts.push('Today');
  if (roles.departure) {
    parts.push(`${guestName(roles.departure)} leaves ${roles.departure.departure_time}`);
    if (roles.departure.access_revoked) parts.push('guest access revoked');
  }
  const n = roles.night;
  if (n) {
    if (n.first) {
      const money = compactMoney(n.stay.gross_cents, n.stay.currency);
      parts.push(`${guestName(n.stay)} arrives ${n.stay.arrival_time}`, sourceText(n.stay.source), nightsLabel(n.stay.nights));
      if (money) parts.push(money);
    } else {
      parts.push(`${guestName(n.stay)} staying, night ${n.index} of ${n.of}`);
    }
    if (n.stay.access_revoked && (!roles.departure || roles.departure.id !== n.stay.id)) {
      parts.push('guest access revoked');
    }
  }
  if (roles.block) parts.push(`Closed: ${(roles.block.note || '').trim() || 'Owner block'}`);
  if (roles.provider) parts.push('Channel block: unavailable');
  if (roles.gap) parts.push(gapText(roles.gap.nights));
  const footer = footerOf(roles, index, data);
  if (footer) {
    parts.push(
      footer.kind === 'turnaround'
        ? footer.text.replace(' · ', ', ').replace(/ h$/, ' hours')
        : 'Clean, then ready'
    );
  }

  return parts.join(', ');
};

/* ─────────────────────────────  ranges  ───────────────────────────── */

/** An inclusive on-screen selection, in order. */
export const orderRange = (a: string, b: string): { start: string; end: string } =>
  a <= b ? { start: a, end: b } : { start: b, end: a };

/** "14 Oct" */
export const shortDay = (day: string): string => formatDayKey(day);

/** "14–17 Oct", or "28 Oct – 2 Nov" across a month. */
export const spanText = (start: string, end: string): string => {
  if (start.slice(0, 7) === end.slice(0, 7)) {
    return `${Number(start.slice(8, 10))}–${shortDay(end)}`;
  }

  return `${shortDay(start)} – ${shortDay(end)}`;
};

/** The live stays with a night in [start, endExclusive). */
export const staysInRange = (data: StayCalendar, start: string, endExclusive: string): StayCalendarReservation[] =>
  data.reservations
    .filter(r => r.check_in < endExclusive && r.check_out > start)
    .sort((a, b) => (a.check_in < b.check_in ? -1 : 1));

/** The blocks of one kind with a night in [start, endExclusive). */
export const blocksInRange = (
  data: StayCalendar,
  start: string,
  endExclusive: string,
  kind: StaysBlock['kind'] = 'owner'
): StaysBlock[] =>
  data.blocks
    .filter(b => b.kind === kind && b.start_date < endExclusive && b.end_date > start)
    .sort((a, b) => (a.start_date < b.start_date ? -1 : 1));

/** "Painting · 24 Oct → 27 Oct" (the end is the day the dates open again). */
export const blockLine = (b: StaysBlock): string =>
  `${(b.note || '').trim() || 'Owner block'} · ${shortDay(b.start_date)} → ${shortDay(b.end_date)}`;
